import React, {
  Suspense, lazy, useEffect, useMemo, useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { buildTraceFailureSuggestions, summarizeTraceEvents } from '../../utils/studioV2TraceRollup';
import { extractRunPayload } from '../../utils/studioV2NodeInspector';
import StudioV2NodeInspector from './StudioV2NodeInspector';

const DebugPanel = lazy(() => import('../AgenticFlow/DebugPanel'));

export function studioV2LastRunStorageKey(flowMongoId) {
  return `studioV2_lastRun_${flowMongoId}`;
}

function pickExecutionId(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null;
  if (snapshot.executionId) return String(snapshot.executionId);
  const d = snapshot.data;
  if (d && typeof d === 'object') {
    if (d.execution_id) return String(d.execution_id);
    if (d.executionId) return String(d.executionId);
  }
  return null;
}

/**
 * Debug hub: last run snapshot, optional legacy DebugPanel when an execution id is known.
 */
export default function StudioV2FlowDebugPanel({
  flowMongoId,
  definitionText = '',
  autoOpenDebug = false,
  onAutoOpenDebugConsumed,
}) {
  const [last, setLast] = useState(null);
  const [parseErr, setParseErr] = useState(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState('');

  const executionId = useMemo(() => pickExecutionId(last), [last]);
  const requestId = useMemo(() => {
    const d = last?.data;
    return String(last?.requestId || d?.requestId || d?.request_id || '').trim() || null;
  }, [last]);
  const streamEvents = useMemo(() => {
    const events = last?.data?.events;
    return Array.isArray(events) ? events : [];
  }, [last]);
  const langGraphV2Events = useMemo(() => (
    streamEvents.filter((ev) => ev?.langgraph_v2 || ev?.event_type)
  ), [streamEvents]);
  const traceSummary = useMemo(() => summarizeTraceEvents(streamEvents), [streamEvents]);
  const failureSuggestions = useMemo(() => buildTraceFailureSuggestions(streamEvents), [streamEvents]);
  const runPayload = useMemo(() => extractRunPayload(last), [last]);
  const pathRows = useMemo(() => {
    const path = runPayload?.execution_path || [];
    if (!Array.isArray(path) || !path.length) return [];
    return path.map((entry, i) => {
      const nodeId = typeof entry === 'object' ? (entry.node_id || entry.id) : String(entry);
      return {
        key: `path-${nodeId}-${i}`,
        nodeId: String(nodeId),
        status: (typeof entry === 'object' ? entry.status : null) || 'completed',
        duration: null,
        eventCount: 0,
        llmEvents: 0,
        toolEvents: 0,
      };
    });
  }, [runPayload]);
  const timelineRows = traceSummary.rows.length ? traceSummary.rows : pathRows;
  const showTimeline = timelineRows.length > 0;

  useEffect(() => {
    if (autoOpenDebug && executionId) {
      setDebugOpen(true);
      onAutoOpenDebugConsumed?.();
    }
  }, [autoOpenDebug, executionId, onAutoOpenDebugConsumed]);

  const refresh = () => {
    setParseErr(null);
    try {
      const raw = flowMongoId ? sessionStorage.getItem(studioV2LastRunStorageKey(flowMongoId)) : null;
      if (!raw) {
        setLast(null);
        return;
      }
      setLast(JSON.parse(raw));
    } catch (e) {
      setParseErr(e.message);
      setLast(null);
    }
  };

  useEffect(() => {
    refresh();
  }, [flowMongoId]);

  const copyDebugBundle = async () => {
    const bundle = {
      source: 'studio-v2-flow-debug',
      copiedAt: new Date().toISOString(),
      flowMongoId: flowMongoId || null,
      executionId: executionId || null,
      requestId: requestId || null,
      definitionText: definitionText || '',
      lastSnapshot: last || null,
      traceSummary,
      failureSuggestions,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(bundle, null, 2));
    } catch (e) {
      setParseErr(e?.message || 'Could not copy debug bundle');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1" fontWeight={700}>Run &amp; debug</Typography>
        <Typography variant="body2" color="text.secondary">
          Use
          {' '}
          <strong>Run</strong>
          {' '}
          in the header for sync or streaming.
          Enable
          {' '}
          <strong>Step-through debug</strong>
          {' '}
          to pause before each node and inspect state with Step / Run to end (F10).
          Click a row in the trace timeline to open the
          {' '}
          <strong>Node inspector</strong>
          {' '}
          (output, events, variables, context).
          Save named inputs under
          {' '}
          <strong>Debug profiles</strong>
          {' '}
          on the Studio home page.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" onClick={refresh} sx={{ textTransform: 'none' }}>
            Refresh last result
          </Button>
          {flowMongoId && (
            <Button
              size="small"
              color="error"
              variant="text"
              onClick={() => {
                sessionStorage.removeItem(studioV2LastRunStorageKey(flowMongoId));
                setLast(null);
                setDebugOpen(false);
              }}
              sx={{ textTransform: 'none' }}
            >
              Clear cached result
            </Button>
          )}
          {executionId && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={() => setDebugOpen((o) => !o)}
              sx={{ textTransform: 'none' }}
            >
              {debugOpen ? 'Hide debug panel' : 'Open debug panel'}
            </Button>
          )}
          {last && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={copyDebugBundle}
              sx={{ textTransform: 'none' }}
            >
              Copy debug bundle
            </Button>
          )}
        </Stack>
        {parseErr && <Alert severity="warning">{parseErr}</Alert>}
        {!last && !parseErr && (
          <Typography variant="body2" color="text.disabled">
            No cached run for this flow yet. Run once from the Run dialog — the latest response (including
            {' '}
            <code>execution_id</code>
            ) is stored in this browser.
          </Typography>
        )}
        {last && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Last run (
              {last.t ? new Date(last.t).toLocaleString() : 'unknown time'}
              )
              {executionId && (
                <>
                  {' '}
                  ·
                  {' '}
                  <code>{executionId}</code>
                </>
              )}
              {requestId && (
                <>
                  {' '}
                  · req
                  {' '}
                  <code>{requestId}</code>
                </>
              )}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5, maxHeight: 280, overflow: 'auto', bgcolor: 'action.hover',
              }}
            >
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 11,
                }}
              >
                {JSON.stringify(last.data ?? last, null, 2)}
              </Typography>
            </Paper>
          </Box>
        )}
        {langGraphV2Events.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" fontWeight={700}>LangGraph v2 stream metadata</Typography>
                <Chip size="small" color="success" label={`${langGraphV2Events.length} events`} />
                <Chip size="small" variant="outlined" label="astream_events v2" />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Recent typed stream events cached from the latest Studio stream run. Use this to inspect event type, node, run id, tags, and metadata.
              </Typography>
              <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                {langGraphV2Events.slice(-40).map((ev, i) => {
                  const meta = ev.langgraph_v2 || {};
                  const eventType = meta.event || ev.event_type || ev.type || 'event';
                  const name = meta.name || ev.node_id || ev.nodeId || '';
                  const runId = meta.run_id || ev.run_id || '';
                  return (
                    <Paper key={`${eventType}-${name}-${i}`} variant="outlined" sx={{ p: 1, mb: 0.75, bgcolor: 'background.default' }}>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                        <Chip size="small" label={eventType} color={eventType.includes('error') ? 'error' : eventType.includes('end') || ev.type === 'node_complete' ? 'success' : 'default'} />
                        {name && <Chip size="small" variant="outlined" label={name} />}
                        {runId && <Chip size="small" variant="outlined" label={`run ${String(runId).slice(0, 8)}`} />}
                        {Array.isArray(meta.tags) && meta.tags.slice(0, 3).map((tag) => <Chip key={tag} size="small" variant="outlined" label={tag} />)}
                      </Stack>
                      {meta.metadata && Object.keys(meta.metadata).length > 0 && (
                        <Typography
                          component="pre"
                          variant="caption"
                          sx={{
                            mt: 0.75, mb: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 11,
                          }}
                        >
                          {JSON.stringify(meta.metadata, null, 2)}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            </Stack>
          </Paper>
        )}
        {showTimeline && (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" fontWeight={700}>Trace timeline</Typography>
                {streamEvents.length > 0 && (
                  <>
                    <Chip size="small" label={`${traceSummary.eventCount} events`} />
                    <Chip size="small" label={`${traceSummary.totalTime.toFixed(2)}s total`} color="info" />
                  </>
                )}
                {!streamEvents.length && pathRows.length > 0 && (
                  <Chip size="small" variant="outlined" label="from execution_path" />
                )}
                {traceSummary.slowestNode && (
                  <Chip size="small" variant="outlined" label={`slowest: ${traceSummary.slowestNode.nodeId} ${traceSummary.slowestNode.duration?.toFixed?.(2)}s`} />
                )}
              </Stack>
              {failureSuggestions.length > 0 && (
                <Alert severity="warning">
                  {failureSuggestions.map((s) => `${s.title}: ${s.detail}`).join(' ')}
                </Alert>
              )}
              <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
                {timelineRows.map((row) => {
                  const selected = selectedNodeId === row.nodeId;
                  return (
                  <Paper
                    key={row.key}
                    variant="outlined"
                    onClick={() => setSelectedNodeId(row.nodeId)}
                    sx={{
                      p: 1,
                      mb: 0.75,
                      cursor: 'pointer',
                      bgcolor: selected
                        ? 'action.selected'
                        : (row.status === 'error' ? 'rgba(211,47,47,0.08)' : 'background.default'),
                      borderColor: selected ? 'primary.main' : 'divider',
                    }}
                  >
                    <Stack spacing={0.75}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={row.status} color={row.status === 'error' ? 'error' : row.status === 'completed' ? 'success' : 'default'} />
                        <Typography variant="body2" fontFamily="monospace" fontWeight={700}>{row.nodeId}</Typography>
                        {Number.isFinite(row.duration) && <Chip size="small" variant="outlined" label={`${row.duration.toFixed(2)}s`} />}
                        {row.llmEvents > 0 && <Chip size="small" variant="outlined" label={`LLM ${row.llmEvents}`} />}
                        {row.toolEvents > 0 && <Chip size="small" variant="outlined" label={`Tool ${row.toolEvents}`} />}
                        <Chip size="small" variant="outlined" label={`${row.eventCount} events`} />
                      </Stack>
                      <Box sx={{
                        height: 6, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden',
                      }}
                      >
                        <Box sx={{
                          height: '100%',
                          width: `${Math.max(3, Math.min(100, ((row.duration || 0) / Math.max(traceSummary.slowestNode?.duration || 1, 1)) * 100))}%`,
                          bgcolor: row.status === 'error' ? 'error.main' : 'primary.main',
                        }}
                        />
                      </Box>
                      {row.lastError && <Typography variant="caption" color="error">{row.lastError}</Typography>}
                    </Stack>
                  </Paper>
                  );
                })}
              </Box>
              <StudioV2NodeInspector
                nodeId={selectedNodeId}
                events={streamEvents}
                runPayload={runPayload}
              />
            </Stack>
          </Paper>
        )}

        <Collapse in={debugOpen && !!executionId}>
          <Box sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'auto',
            maxHeight: 'min(72vh, 760px)',
          }}
          >
            <Suspense fallback={(
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">Loading debug UI…</Typography>
              </Box>
            )}
            >
              <DebugPanel
                executionId={executionId}
                flowId={flowMongoId}
                streamEvents={streamEvents}
                embedded
                hideChromeClose
                onClose={() => setDebugOpen(false)}
                onComplete={() => refresh()}
              />
            </Suspense>
          </Box>
        </Collapse>
      </Stack>
    </Paper>
  );
}
